import { useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import {
  History,
  FileCode,
  Table,
  Server,
  Clock,
  RotateCcw,
  Trash2,
  ChevronDown,
  ChevronRight,
  FileSearch,
} from 'lucide-react';
import { useTabHistoryStore } from '../store/tabHistoryStore';
import { useWorkspaceStore } from '../../workspace';
import type { TabHistoryEntry, TabHistoryGroup } from '../types/tabHistoryTypes';
import type { Tab } from '../../workspace/core/types/workspaceTypes';
import { useState } from 'react';
import { tabHistoryEntryToQueryContent } from '../utils/tabHistoryReopen';
import { safeServerText } from '../../../types/safeConnectionDisplay';
import { safeTabTitle } from '../../workspace/core/store/safeTabText';

/**
 * Format relative time (e.g., "2 minutes ago", "1 hour ago")
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Truncate text for display
 */
function truncateText(text: string, maxLength = 60): string {
  const trimmed = text.trim().split('\n')[0]; // First line only
  if (trimmed.length <= maxLength) return trimmed;
  return trimmed.slice(0, maxLength - 3) + '...';
}

function getFileNameFromPath(filePath: string): string {
  const parts = filePath.split(/[/\\]/);
  return parts[parts.length - 1] || filePath;
}

function TabHistoryItem({
  entry,
  onReopen,
}: {
  entry: TabHistoryEntry;
  onReopen: () => void;
}) {
  const Icon = entry.type === 'query' ? FileCode : entry.type === 'plan' ? FileSearch : Table;
  const subtitle = entry.type === 'query'
    ? entry.query ? truncateText(entry.query) : entry.queryOmitted ? '(script too large to keep)' : '(empty query)'
    : entry.type === 'plan'
      ? entry.planView === 'compare'
        ? `Compare: ${entry.baselineFilePath ? getFileNameFromPath(entry.baselineFilePath) : '(baseline)'} ↔ ${entry.compareFilePath ? getFileNameFromPath(entry.compareFilePath) : '(compare)'}`
        : entry.filePath ? getFileNameFromPath(entry.filePath) : '(no plan file)'
      : entry.tableName
        ? `${entry.schema}.${entry.tableName}`
        : '(no table selected)';

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors group border-b border-border/30 last:border-b-0"
      data-testid={`history-tab-row-${entry.id}`}
    >
      {/* Icon */}
      <span className="flex-shrink-0 text-muted-foreground">
        <Icon className="h-4 w-4" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{safeTabTitle(entry.title)}</div>
        <div className="text-xs text-muted-foreground truncate font-mono">
          {subtitle}
        </div>
      </div>

      {/* Time */}
      <span className="flex-shrink-0 text-[10px] text-muted-foreground/60 flex items-center gap-1">
        <Clock className="h-2.5 w-2.5" />
        {formatRelativeTime(entry.closedAt)}
      </span>

      {/* Re-open button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={onReopen}
        className="h-6 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
        data-testid={`history-tab-reopen-${entry.id}`}
      >
        <RotateCcw className="h-3 w-3 mr-1" />
        Re-open
      </Button>
    </div>
  );
}

function OpenTabItem({
  tab,
  onActivate,
}: {
  tab: Tab;
  onActivate: () => void;
}) {
  const Icon = tab.type === 'query' ? FileCode : tab.type === 'plan' ? FileSearch : Table;
  const subtitle = tab.type === 'query'
    ? tab.content.query ? truncateText(tab.content.query) : '(empty query)'
    : tab.type === 'plan'
      ? tab.content.filePath ? getFileNameFromPath(tab.content.filePath) : '(no plan file)'
      : tab.type === 'security'
        ? tab.content.objectType ?? 'Security Manager'
        : 'tableName' in tab.content && tab.content.tableName
          ? `${tab.content.schema}.${tab.content.tableName}`
          : '(no table selected)';

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors cursor-pointer border-b border-border/30 last:border-b-0"
      onClick={onActivate}
    >
      {/* Icon with "open" indicator */}
      <span className="flex-shrink-0 text-primary">
        <Icon className="h-4 w-4" />
      </span>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{safeTabTitle(tab.title)}</div>
        <div className="text-xs text-muted-foreground truncate font-mono">
          {subtitle}
        </div>
      </div>

      {/* Open indicator */}
      <span className="text-[10px] text-primary/70 font-medium">OPEN</span>
    </div>
  );
}

function ConnectionGroup({
  group,
  openTabs,
  onReopenTab,
  onActivateTab,
  defaultExpanded = true,
}: {
  group: TabHistoryGroup;
  openTabs: Tab[];
  onReopenTab: (entry: TabHistoryEntry) => void;
  onActivateTab: (tabId: string) => void;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const totalCount = openTabs.length + group.closedTabs.length;

  return (
    <div className="border border-border/50 rounded-md overflow-hidden mb-2">
      {/* Group header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
        <Server className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">{safeServerText(group.connectionName) || group.connectionName}</span>
        {group.serverHost && (
          <span className="text-xs text-muted-foreground">
            ({safeServerText(group.serverHost)})
          </span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {totalCount} tab{totalCount !== 1 ? 's' : ''}
        </span>
      </button>

      {/* Group content */}
      {isExpanded && (
        <div className="divide-y divide-border/30">
          {/* Open tabs first */}
          {openTabs.map((tab) => (
            <OpenTabItem
              key={tab.id}
              tab={tab}
              onActivate={() => onActivateTab(tab.id)}
            />
          ))}
          {/* Then closed tabs */}
          {group.closedTabs.map((entry) => (
            <TabHistoryItem
              key={entry.id}
              entry={entry}
              onReopen={() => onReopenTab(entry)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TabHistoryDialog() {
  const isDialogOpen = useTabHistoryStore((state) => state.isDialogOpen);
  const closeDialog = useTabHistoryStore((state) => state.closeDialog);
  const removeClosedTab = useTabHistoryStore((state) => state.removeClosedTab);
  const clearClosedTabs = useTabHistoryStore((state) => state.clearClosedTabs);
  // Subscribe to actual data to trigger re-renders when it changes
  const closedTabsBySession = useTabHistoryStore((state) => state.closedTabsBySession);
  const currentSessionId = useTabHistoryStore((state) => state.currentSessionId);

  const tabs = useWorkspaceStore((state) => state.tabs);
  const addTab = useWorkspaceStore((state) => state.addTab);
  const setActiveTab = useWorkspaceStore((state) => state.setActiveTab);

  // Group closed tabs - recomputes when closedTabsBySession or currentSessionId changes
  const closedTabGroups = useMemo(() => {
    if (!currentSessionId) return [];
    const closedTabs = closedTabsBySession[currentSessionId] || [];
    const groups: Map<string, TabHistoryGroup> = new Map();

    for (const tab of closedTabs) {
      const connectionId = tab.connectionInfo?.connectionId || 'no-connection';
      const existing = groups.get(connectionId);

      if (existing) {
        existing.closedTabs.push(tab);
      } else {
        groups.set(connectionId, {
          connectionId,
          connectionName: tab.connectionInfo?.connectionName || 'No Connection',
          serverHost: tab.connectionInfo?.serverHost,
          closedTabs: [tab],
        });
      }
    }

    return Array.from(groups.values());
  }, [closedTabsBySession, currentSessionId]);

  // Group open tabs by connection
  const openTabsByConnection = useMemo(() => {
    const groups: Map<string, Tab[]> = new Map();
    for (const tab of tabs) {
      const connId = tab.connectionInfo?.connectionId || 'no-connection';
      const existing = groups.get(connId) || [];
      groups.set(connId, [...existing, tab]);
    }
    return groups;
  }, [tabs]);

  // Merge open and closed tab groups
  const allGroups = useMemo(() => {
    const groupMap: Map<string, { group: TabHistoryGroup; openTabs: Tab[] }> = new Map();

    // Add closed tab groups
    for (const group of closedTabGroups) {
      groupMap.set(group.connectionId, { group, openTabs: [] });
    }

    // Add open tabs to existing groups or create new ones
    for (const [connId, openTabs] of openTabsByConnection) {
      const existing = groupMap.get(connId);
      if (existing) {
        existing.openTabs = openTabs;
      } else {
        // Create a group from open tabs
        const firstTab = openTabs[0];
        groupMap.set(connId, {
          group: {
            connectionId: connId,
            connectionName: firstTab.connectionInfo?.connectionName || 'No Connection',
            serverHost: firstTab.connectionInfo?.serverHost,
            closedTabs: [],
          },
          openTabs,
        });
      }
    }

    return Array.from(groupMap.values());
  }, [closedTabGroups, openTabsByConnection]);

  const handleReopenTab = useCallback((entry: TabHistoryEntry) => {
    // Create a new tab with the saved content
    if (entry.type === 'query') {
      addTab({
        title: entry.title,
        customTitle: entry.customTitle,
        type: 'query',
        connectionInfo: entry.connectionInfo,
        content: tabHistoryEntryToQueryContent(entry),
      });
    } else if (entry.type === 'table-explorer') {
      addTab({
        title: entry.title,
        customTitle: entry.customTitle,
        type: 'table-explorer',
        connectionInfo: entry.connectionInfo,
        content: {
          tableName: entry.tableName || '',
          schema: entry.schema || 'dbo',
          database: entry.database || '',
        },
      });
    } else if (entry.type === 'plan') {
      addTab({
        title: entry.title,
        customTitle: entry.customTitle,
        type: 'plan',
        content: {
          view: entry.planView ?? 'single',
          filePath: entry.filePath,
          baselineFilePath: entry.baselineFilePath,
          compareFilePath: entry.compareFilePath,
        },
      });
    }

    // Remove from closed tabs
    removeClosedTab(entry.id);

    // Close dialog
    closeDialog();
  }, [addTab, removeClosedTab, closeDialog]);

  const handleActivateTab = useCallback((tabId: string) => {
    setActiveTab(tabId);
    closeDialog();
  }, [setActiveTab, closeDialog]);

  const totalClosedTabs = closedTabGroups.reduce(
    (sum, group) => sum + group.closedTabs.length,
    0
  );

  return (
    <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Tab History
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-2">
          {allGroups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No tabs in history</p>
              <p className="text-xs mt-1">
                Open and closed tabs will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {allGroups.map(({ group, openTabs }) => (
                <ConnectionGroup
                  key={group.connectionId}
                  group={group}
                  openTabs={openTabs}
                  onReopenTab={handleReopenTab}
                  onActivateTab={handleActivateTab}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {totalClosedTabs > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {totalClosedTabs} closed tab{totalClosedTabs !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearClosedTabs}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />
              Clear History
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
